/**
 * API модуль для событий безопасности (log-events).
 * Эндпоинты из qhub-gateway: /v2/log-events.
 */

import { entityPath, type EntityId } from '@/lib/entityId';
import { api } from './client';
import type { AccessConditionCode } from './accessPolicyTypes';
import type { EventSeverity, PaginationParams, SortParams } from './types';
import { parseEventWorkflow, parseResourceRefs } from './workspaceAdapters';
import type { EventWorkflow, ResourceRef, WorkflowStatus } from './workspaceTypes';

/**
 * Шкала важности событий — пять уровней (решение владельца 6, 25.10.2026). У события до снимка
 * важности уровня нет: интерфейс показывает прочерк и шестого значения не вводит.
 */
export const EVENT_SEVERITIES = [
	'critical',
	'high',
	'medium',
	'low',
	'info',
] as const satisfies readonly EventSeverity[];

/* ────────────────────────────────────────────
 * Типы
 * ──────────────────────────────────────────── */

/** Ассет (сервис) в событии */
export interface EventAsset {
	id: number;
	name: string;
	namespace?: string;
	type?: string;
	outbound_facing?: boolean;
	cluster_name?: string;
}

/** Событие безопасности */
export interface LogEvent {
	id: number;
	type: string;
	created_at: number;
	/** Прежнее поле: сервер его не отдаёт, важность — severity */
	sensitivity?: string;
	/** Важность на момент создания (E2.5a); null — событие до снимка важности */
	severity?: EventSeverity | null;
	severity_source?: 'policy' | 'event_type' | 'legacy_unknown';
	/** Рабочие числовые и изолированные учебные строковые ID справочника. */
	data_types: EntityId[];
	is_encrypted: boolean;
	is_mesh_network?: boolean;
	is_network_encrypted?: boolean;
	region?: string;
	/** Название/описание — зависит от type */
	title?: string;
	subtitle?: string;
	service_name?: string;
	endpoint?: string;
	endpoint_id?: number;
	source?: string;
	destination?: string;
	/** DIM-специфичные поля */
	method?: string;
	url?: string;
	host?: string;
	hostname?: string;
	protocol?: string;
	json_path?: string;
	external_ip?: string;
	user_agent?: string;
	event_regarding?: 'requestor' | 'responder';
	direction?: 'request' | 'response' | 'incoming' | 'outgoing' | 'internal';
	requestor?: EventAsset;
	responder?: EventAsset;
	/** DAR-специфичные поля */
	object_type?: string;
	s3_bucket?: { id?: number; name: string; region?: string; cluster_id?: number };
	kafka?: {
		instance_id?: number;
		instance_name?: string;
		topic_id?: number;
		topic_name?: string;
		scheme_id?: number;
		schema_name?: string;
	};
	database?: {
		database_id?: number;
		database_name?: string;
		instance_name?: string;
		table_id?: number;
		table_name?: string;
		type?: string;
	};
	nosql_database?: {
		database_id?: number;
		database_name?: string;
		instance_name?: string;
		collection_id?: number;
		collection_name?: string;
		type?: string;
	};
	/** DB Access */
	db_access?: {
		user_name?: string;
		auth_source?: string;
		databases?: Array<{ id: number; name: string; type?: string }>;
	};
	/** Политики */
	policy_id?: number;
	policy_name?: string;
	policy_tags?: string[];
	/** Custom assets */
	custom_assets?: Array<{ id: number; name: string }>;
	/** Sample данные; null при sample_restricted — у пользователя нет права samples.read */
	sample?: unknown;
	sample_restricted?: boolean;
	/** Разбор события (E2.4): есть у каждой строки списка и у карточки */
	workflow?: EventWorkflow;
	/** Карточка (E2.4): ресурсы и находки своей компании, id находки — строкой без сужения */
	resource_refs?: ResourceRef[];
	finding_ids?: string[];
	/** Нарушение политики доступа (E3.12): условие и доказательство — в карточке события */
	access_violation?: AccessViolation;
}

/**
 * Доказательство нарушения политики доступа: право снимка (задача сбора, субъект, действие
 * провайдера) или группы ресурса для размещения вне групп
 */
export interface AccessViolationEvidence {
	source: string | null;
	principal: string | null;
	action: string | null;
	group_ids: string[];
}

export interface AccessViolation {
	condition: AccessConditionCode;
	snapshot_id: string;
	evidence: AccessViolationEvidence[];
}

/** Параметры фильтрации событий */
export interface EventFilters extends PaginationParams, SortParams {
	/** Важности через запятую из EVENT_SEVERITIES */
	severity?: string;
	/** Поиск по типу, политике, ресурсу и ответственному — на сервере */
	q?: string;
	type?: string;
	from?: number;
	to?: number;
	regions?: string;
	cluster_ids?: string;
	namespaces?: string;
	resource_types?: string;
	policy_tags?: string;
	data_type_ids?: string;
	/** Состояния разбора через запятую: new, in_progress, closed */
	workflow_status?: string;
}

/** Ответ списка событий: total — все события компании, total_filtered — по фильтру */
export interface LogEventsResponse {
	log_events: LogEvent[];
	total: number;
	total_filtered: number;
}

/** Счётчики по severity */
export interface EventCounters {
	total: number;
	critical: number;
	high: number;
	medium: number;
	low: number;
	info: number;
	/** События до снимка важности */
	unknown: number;
}

/** Элемент разбивки по типу событий */
export interface EventBreakdownItem {
	title: string;
	count: number;
	percent: number;
}

/** Категория события */
export type EventCategory =
	| 'infrastructure_updates'
	| 'data_at_risk'
	| 'misconfiguration'
	| 'other';

/** Ответ статистики событий */
export interface EventStatisticResponse {
	counters_filtered: EventCounters;
	breakdown_by_event: EventBreakdownItem[];
	/** События по состоянию разбора — по тем же фильтрам, кроме фильтра состояния */
	by_workflow_status: Record<WorkflowStatus, number>;
	/** Критические события, которые ещё не закрыты */
	critical_open: number;
	/** Закрытые за текущий период: последний переход в «закрыто» — в окне сервера (7 суток) */
	closed_in_period: number;
}

/* ────────────────────────────────────────────
 * API функции
 * ──────────────────────────────────────────── */

const EVENTS_BASE = '/v2/log-events';

/** Разбор и ссылки события проверяются контрактом; прежние поля остаются как пришли */
function withWorkflow(event: LogEvent, path: string): LogEvent {
	const parsed: LogEvent = { ...event };
	if (event.workflow !== undefined) {
		parsed.workflow = parseEventWorkflow(event.workflow, `${path}.workflow`);
	}
	if (event.resource_refs !== undefined) {
		parsed.resource_refs = parseResourceRefs(event.resource_refs, `${path}.resource_refs`);
	}
	return parsed;
}

/** Получение списка событий с пагинацией и фильтрацией */
export async function fetchEvents(params?: EventFilters): Promise<LogEventsResponse> {
	const response = await api.get<LogEventsResponse>(
		EVENTS_BASE,
		params as Record<string, string | number>,
	);
	return {
		...response,
		log_events: response.log_events.map((event, index) =>
			withWorkflow(event, `log_events[${index}]`),
		),
	};
}

/** Получение одного события по id */
export async function fetchEvent(id: EntityId): Promise<LogEvent> {
	return withWorkflow(await api.get<LogEvent>(`${EVENTS_BASE}/${entityPath(id)}`), 'event');
}

/** Получение sample данных события */
export async function fetchEventSample(id: EntityId): Promise<unknown> {
	return api.get<unknown>(`${EVENTS_BASE}/${entityPath(id)}/sample`);
}

/** Получение статистики событий */
export async function fetchEventStatistic(
	params?: Omit<EventFilters, 'limit' | 'offset'>,
): Promise<EventStatisticResponse> {
	return api.get<EventStatisticResponse>(
		`${EVENTS_BASE}/statistic`,
		params as Record<string, string | number>,
	);
}
